"use client";
import { useState, useEffect, Suspense } from "react";
import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { FaFacebookF } from "react-icons/fa";

const LOGIN_ERROR_MESSAGES = {
  customer_only:
    "Энэ сайт зөвхөн хэрэглэгчдэд зориулагдсан. Түнш нэвтрэлтийг бизнес порталаас ашиглана уу.",
  no_email: "Имэйл олдсонгүй. Нэвтрэхийн тулд имэйлтэй бүртгэл ашиглана уу.",
  server: "Серверийн алдаа гарлаа. Дахин оролдоно уу.",
};

const CREDENTIALS_ERROR_MESSAGES = {
  CredentialsSignin: "Имэйл эсвэл нууц үг буруу байна.",
  Configuration:
    "Нэвтрэлтийн тохиргоо буруу байна. NEXTAUTH_URL нь одоогийн сайтын хаягтай (жишээ нь порт 3001) тааруулсан эсэхийг шалгана уу.",
  AccessDenied: "Нэвтрэх зөвшөөрөлгүй.",
};

function LoginErrorBanner() {
  const searchParams = useSearchParams();
  const code = searchParams.get("error");
  if (!code) return null;
  const message =
    LOGIN_ERROR_MESSAGES[code] ?? "Нэвтрэлт амжилтгүй болсон.";
  return (
    <div
      role="alert"
      className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
    >
      {message}
    </div>
  );
}

function OAuthButtons() {
  const [providers, setProviders] = useState(null);

  useEffect(() => {
    fetch("/api/auth/providers")
      .then((r) => r.json())
      .then((p) => setProviders(p && typeof p === "object" ? p : {}))
      .catch(() => setProviders({}));
  }, []);

  if (!providers || (!providers.google && !providers.facebook)) {
    return null;
  }

  return (
    <>
      <div className="my-6 flex items-center">
        <div className="h-[1px] flex-grow bg-gray-200" />
        <span className="px-3 text-sm text-gray-400">эсвэл</span>
        <div className="h-[1px] flex-grow bg-gray-200" />
      </div>
      <div className="space-y-3">
        {providers.google ? (
          <motion.button
            type="button"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => signIn("google")}
            className="flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white py-3 text-gray-700 shadow-md transition hover:bg-gray-50"
          >
            <FcGoogle size={22} />
            <span className="font-medium">Google-ээр нэвтрэх</span>
          </motion.button>
        ) : null}
        {providers.facebook ? (
          <motion.button
            type="button"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => signIn("facebook")}
            className="flex w-full items-center justify-center gap-3 rounded-lg bg-[#1877F2] py-3 text-white shadow-md transition hover:bg-[#145dbf]"
          >
            <FaFacebookF size={20} />
            <span className="font-medium">Facebook-ээр нэвтрэх</span>
          </motion.button>
        ) : null}
      </div>
    </>
  );
}

export default function CustomerLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !password) {
      setFormError("Имэйл болон нууц үгээ оруулна уу.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await signIn("credentials", {
        redirect: false,
        email: trimmed,
        password,
      });
      if (!res?.error) {
        // Бүтэн ачаалал: session cookie шууд дараагийн хүсэгт илгээгдэнэ (router.push+refresh түр зөрөх тохиолдолд 401/403 мэт).
        window.location.assign("/");
        return;
      }
      setFormError(
        CREDENTIALS_ERROR_MESSAGES[res.error] ||
          "Нэвтрэх амжилтгүй. Дахин оролдоно уу."
      );
    } catch {
      setFormError("Сүлжээний алдаа. Дахин оролдоно уу.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center from-blue-50 to-blue-100 px-4 pt-10">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg border border-gray-100"
      >
        <Suspense fallback={null}>
          <LoginErrorBanner />
        </Suspense>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-5">
          {formError ? (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {formError}
            </div>
          ) : null}
          <div>
            <label className="text-sm text-gray-600">Имэйл</label>
            <input
              type="email"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="mt-1 w-full rounded-lg border p-3 outline-none transition-all focus:ring-2 focus:ring-[#577099]"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-sm text-gray-600">Нууц үг</label>
              <Link
                href="/customer/forgot-password"
                className="text-xs font-medium text-[#577099] hover:underline"
              >
                Нууц үг мартсан?
              </Link>
            </div>
            <div className="relative mt-1">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full rounded-lg border p-3 pr-11 outline-none transition-all focus:ring-2 focus:ring-[#577099]"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Нууц үг нуух" : "Нууц үг харуулах"}
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 transition hover:text-gray-700 focus:outline-none focus:text-[#577099]"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: submitting ? 1 : 1.03 }}
            whileTap={{ scale: submitting ? 1 : 0.97 }}
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-[#577099] py-3 font-medium text-white shadow-md transition-all hover:bg-[#4a5f85] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Нэвтэрч байна…" : "Хэрэглэгчээр нэвтрэх"}
          </motion.button>
        </form>

        <OAuthButtons />

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-base text-gray-700 mb-2">
            Шинэ хэрэглэгч үү?
          </p>
          <Link
            href="/customer/register"
            className="block w-full rounded-lg bg-[#577099] py-3 text-center font-medium text-white shadow-md transition-all hover:bg-[#4a5f85]"
          >
            Бүртгэл үүсгэх
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
